// Typed-client helpers — parse-on-boundary
export * from "./api-client";

// Nominal brand for post-safeParse wire bodies (Sibling-E §4 #3c, first
// increment landed with chat-cluster). The transport seam in
// `lib/api/post-json.ts` accepts only `Built<T>` — see header of built.ts.
export * from "./built";

// Opaque brand for "image ref as stored — has NOT passed resolveStorageUrl".
// Lives here (not next to the resolver) because web lib/, mobile lib/ and the
// DTOs below all need ONE type identity — see raw-image-ref.ts header.
export * from "./raw-image-ref";

// Shared types — used by multiple endpoint files
export * from "./shipping-method";

// Single source of truth for "this country requires a non-empty state" —
// see address-state.ts header for the 6-copy drift that justified centralising.
export * from "./address-state";

// Structured ship-from address — replaces legacy free-text `listings.location`
// per ADR_LISTING_SHIP_FROM_ADDRESS.md Phase 2. Used on listing response
// schemas (search-listings, etc.) and any new endpoint exposing a listing's
// resolved origin. The `location` field on input schemas (listing-create,
// listing-patch) is intentionally untouched here — Phase 3 owns input cleanup.
export * from "./ship-from-address";

// Per-endpoint contracts (request + response)
export * from "./checkout-intent";
// CAPTURE-channel payload — discriminated union mirror of `checkout-intent.ts`'s
// `flat_rate | live_rate` axis. Added 2026-06-26 to close the parallel-channel
// data-loss antipattern that produced HANDOFF_EXTERNAL_SHIPPING_CAPTURE_GAP_2026_06_26.
// PR-E Move 4 (Sibling-F · F2, 2026-06-27): `CheckoutPayload` is now
// `CheckoutIntent & CaptureSnapshot` per-branch.
export * from "./capture-snapshot";
export * from "./checkout-payload";
export * from "./checkout-source";
export * from "./capture-order";
// FINALIZE channel — native-wallet (Apple Pay / Google Pay) one-shot
// create+capture for mobile. Fuses the CREATE channel (server resolves
// `intent` → purchase_units) with the CAPTURE channel's post-money
// persistence (`checkoutData`). See finalize-order.ts header.
export * from "./finalize-order";
export * from "./stripe-checkout-session";
export * from "./release-cart";
export * from "./delete-cart-item";
export * from "./chat-link-order";

// Response-side contracts — added 2026-06-08 (closes API_RESPONSE_AUDIT P1)
export * from "./app-min-version";
export * from "./paypal-orders-preview";
export * from "./payments-seller-provider";
export * from "./payments-preferences";
export * from "./listing-meta";
export * from "./auctions-current-bid";
export * from "./cart";

// Cart write-path + settings stripe — added 2026-06-09 (hex-extract + boundary parse)
export * from "./cart-update";
export * from "./cart-sync-login";
export * from "./cart-sync-logout";
export * from "./stripe-me";
export * from "./stripe-connect-onboard";
export * from "./stripe-connect-disconnect";
export * from "./stripe-connect-dashboard-link";

// Money-mutation boundary schemas — added 2026-06-09 (Sibling-A P0 from
// RELIABILITY_ROADMAP §3a / API_REQUEST_BOUNDARY_AUDIT P0)
export * from "./cancel-item";
export * from "./admin-recover-order";
export * from "./admin-shipping-method";

// Pre-mobile-lock boundary schemas — added 2026-06-09 (Sibling-A P1 from
// API_REQUEST_BOUNDARY_AUDIT P1). All 8 routes mobile-consumed (api-orders /
// api-offers / api-paypal / api-kraahl / api-messaging / push), schemas are
// additive-only — every field mobile already sends is accepted unchanged.
export * from "./offers";
export * from "./orders-update-status";
export * from "./orders-update-tracking";
export * from "./cart-reserve";
export * from "./add-to-cart";
export * from "./paypal-account-create-referral";
export * from "./push-tokens";
export * from "./chat-send";
export * from "./chat-upsert";
export * from "./chat-reaction";
export * from "./chat-reply";

// Refund v2 (REFUND_V2.md Phase 3) — request/response contract for the
// goodwill-refund endpoint + refund-state GET. Ledger + triggers shipped in
// Phase 1 (`refund_attempts`, migration `20260709084555`); the domain layer
// and thin routes follow in Phase 3.
// @see .tondocs/payments/REFUND_V2.md §5.1, §5.3
export * from "./refund";

// Provider-agnostic shared enums — added 2026-06-09 (Sibling-C from
// RELIABILITY_ROADMAP §3a). Single source of truth for the normalised
// refund-status discriminator imported by mappers + UI consumers.
export * from "./refund-status";

// Pre-lock web-only money/side-effect boundary schemas — added 2026-06-09
// (Sibling-A P2 from API_REQUEST_BOUNDARY_AUDIT). All 3 routes web-only
// (mobile never hits auction/offer settle or place-bid).
export * from "./auction-settle";
export * from "./offer-settle";
export * from "./auction-place-bid";

// Search wire contracts — added 2026-06-09 (web↔mobile dedup, SEARCH_AUDIT §14.3)
export * from "./search-users";
export * from "./search-listings";
export * from "./search-suggest";

// Suggested sellers — added 2026-07-29, same web↔mobile dedup as search-users.
// Web canonical builder: lib/profile/suggested-sellers.ts
// Mobile builder (not yet migrated onto this schema): apps/mobile/lib/api-profile-suggestions.ts
export * from "./profile-suggested-users";

// Listing creation — added 2026-06-15 (Sibling-E batch 2 F1, RELIABILITY_ROADMAP §3a)
// Web canonical builder: lib/listings/listing-create.ts
// Mobile canonical builder: apps/mobile/lib/listing-create-api.ts
export * from "./listing-create";

// Shipping rates (WS4.1) — provider-neutral quote endpoint
// @see .tondocs/shippo/PLAN.md §D WS4, §L
export * from "./shipping-rates";

// Carrier-minimum parcel floors — single SoT consumed by both the
// listing-publish-time Zod gate (Option A) and the rate-quote-time domain
// gate (Option B). Added 2026-06-30 closing the USPS Priority Mail Intl
// `baseRate` opaque-refusal surface.
// @see .tondocs/shippo/PARCEL_MIN_DIMS.md
export * from "./parcel-min-dims";

// OFAC sanctioned-destination guard (WS-O.2.7) — single SoT for the type-
// level `ShippableCountry` brand + the runtime predicate / assertion used
// at every label-buying boundary.
// @see .tondocs/shippo/PLAN.md §O.2.7, §K.10 axis 9
export * from "./sanctioned-countries";

// Ready-to-ship — seller-action endpoint that re-rates + buys the label sync.
// @see .tondocs/shippo/PLAN.md §L (replaces post-capture async buy)
export * from "./ready-to-ship";

// Label-URL re-fetch — closes §M.8 trip-wire / §O.2.5 P0β gate. Sibling of
// ready-to-ship; serves a fresh Shippo CDN URL when the persisted one 404s.
export * from "./label-url";

// Customs declaration — capture-time snapshot for cross-border orders.
// @see .tondocs/shippo/PROBES_2026_06_25.md §G — 3 builder gates probe
export * from "./customs-declaration";

// Shippo SDK response contracts — parsed envelopes consumed by the adapter
// boundary in `lib/shippo/client.ts` (Sibling-F · F9). One safeParse per SDK
// seam (shipments.create/get, addresses.create, transactions.create/get,
// trackingStatus.get, refunds.create); after parse, downstream code trusts
// the shape (Parse, Don't Validate).
// @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-F · F9
// @see .tondocs/shippo/HANDOFF_RELIABILITY_GAPS_2026_06_27.md PR-F
export * from "./shippo-rate";

// Shippo webhook event contracts — discriminated envelope parsed at
// `app/api/webhooks/shippo/route.ts` (Sibling-F · F8). Closes the
// opaque-wrapper class on the webhook seam (same risk pattern as
// `CaptureOrderRequestSchema.checkoutData` pre-PR-B `42d524eb`).
// @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-F · F8
// @see .tondocs/shippo/HANDOFF_RELIABILITY_GAPS_2026_06_27.md PR-H
export * from "./shippo-webhook";

// Edit-listing hardening — added 2026-06-18 (closes G23/G24 from
// FRONTEND_WIRING_STRATEGY §3.2). PATCH boundary for listing edit save;
// shipping-methods CRUD boundary replacing direct supabase.from() in
// EditShippingTab.tsx / shipping-settings.
// Web canonical builders: lib/listings/update-listing.ts,
//                         lib/listings/shipping-methods-actions.ts
export * from "./listing-patch";
export * from "./listing-shipping-methods";

// Money + currency contracts — added 2026-06-23
// (ADR_CURRENCY_PIN — currency-pin pivoted to `profiles.currency`).
// Iso4217 + Money + CarrierEligibility + ChangeProfileCurrencyResult + Blocker.
// Consumers: lib/currency/*, lib/shipping/carrier-eligibility.ts,
// lib/profile/currency-change.ts, listing create/edit routes + UI.
export * from "./money";

// Carrier-provider const + type — added 2026-06-24 (devil's advocate F-03).
// Single SoT for the `CarrierProvider` discriminator and the canonical
// `CARRIER_PROVIDERS` tuple used by Zod schemas (listing-create/-patch),
// route defaults, eligibility predicates, and DB CHECK comment-pointers.
// Consumers: lib/shipping/*, app/api/listings/[id]/route.ts, listing schemas.
export * from "./carrier";

// Address book read — added 2026-06-23 (per-listing ship-from PR #310).
// First consumer = auto-inferred-address banner under
// `components/shipping-settings/**` (in HARD GATE fence scope, so no inline
// fetch allowed). Minimal pin — widen as other consumers migrate.
// Web canonical builder: lib/address-book/list-addresses.ts
export * from "./address-book";

export * from "./seller-setup";
export * from "./phone-verification";
export * from "./user-currency";

// Listing CATEGORY discriminator — Wave 0 of NEW_CATEGORIES_PLAN (2026-08-02).
// Canonical `LISTING_CATEGORIES` tuple + `unreachableCategory` probe +
// `parseListingCategory` boundary parser. Web `types/listing.ts` and mobile
// `apps/mobile/components/sell-flow/types.ts` re-export from here — no local
// literal unions (guarded by tests/unit/fitness-listing-category.test.ts).
// @see .tondocs/listings/NEW_CATEGORIES_PLAN.md §3.1
export * from "./listing-category";

// Comic category vocabulary + typed draft (Wave 2 of the comics slice,
// 2026-08-02). Tuples mirror the CHECKs on `listing_comic_details`
// (migration 20260802194900); ComicDraftSchema parses on the boundary —
// no opaque record like cardDraft.
export * from "./listing-comic";

// Listing ADT — Phase 1 seed (2026-06-29). Canonical discriminated union for
// the `listings` discriminator-hygiene refactor. PURELY ADDITIVE — no existing
// consumer reads from it yet (Phase 2-7 of the `shippo-listing-discriminator`
// PR materialise the downstream swap). Single boundary parser
// (`parseListingFromRow`) is the SoT for ADT construction.
// @see .tondocs/listings/DISCRIMINATORS_HYGIENE_IMPACT.md §4a, §5a (Phase 1)
export * from "./listing-adt";

// Presentation-layer canonical helpers — Phase 7 of `shippo-listing-discriminator`
// (2026-06-30). Single SoT for the item-card `stateId` enum + derivation, and
// for the story-cards `kind` discriminator selection. Both web
// (`lib/build-item-card-view.ts`, `lib/story-cards/types.ts`) and mobile
// (`apps/mobile/components/ui/item-card.tsx`,
// `apps/mobile/lib/build-listing-item-card-view.ts`) import these helpers —
// no local copies of the state union, no parallel if/else chains. Closes the
// two-disagreeing-state-machine debt documented in DISCRIMINATORS.md §3.2.
// @see .tondocs/listings/DISCRIMINATORS_HYGIENE_IMPACT.md §3.G, §H Phase 7
export * from "./item-card-state";
export * from "./story-context-kind";

// Order display status — PR #348 presentation-layer merge of the two order
// status axes (`seller_orders.status` × `tracking_status` × `label_status`)
// into ONE badge value, lifted here for the mobile mirror
// (DISPLAY_STATUS.md §10). Derive fn + `TrackingStatus` union + i18n label-key
// table; platform visual config (icons/classes) stays local — web:
// `components/orders/_components/order-status-display.tsx`. Same
// one-canonical-declaration precedent as item-card-state above.
// @see .tondocs/orders/DISPLAY_STATUS.md
export * from "./order-display-status";

// Demand-funnel instrumentation B1 (2026-07-14) — listing-view tracking
// beacon. `POST /api/track/listing-view`, canonical builder
// `lib/analytics/track-listing-view.ts`. See track-listing-view.ts header
// for the deliberate `guestId`-on-wire deviation from a stricter
// `{listingId}`-only reading of the spec.
export * from "./track-listing-view";

// Demand-funnel instrumentation B5 (2026-07-14) — batched feed/search card
// impressions. `POST /api/track/impressions`, canonical producers per
// platform: web `lib/analytics/impression-buffer.ts`, mobile
// `apps/mobile/lib/impression-buffer.ts` (batching buffers, NOT per-event
// beacons — impressions run 10-50x view volume). Separate event class from
// track-listing-view.
export * from "./track-impressions";

// Pure decision core shared by BOTH platform buffer shells (throttle /
// flush-threshold / batch-slicing semantics). Lives here so web + mobile
// import ONE canonical declaration — same precedent as item-card-state.
export * from "./impression-buffer-core";

// WHICH CLIENT produced a tracking event (web | mobile) — mobile-tracking
// increment 2026-07-14. Server defaults omitted -> 'web'.
export * from "./tracking-client";

// Buyer "confirm receipt" gate + the legacy status alias map it runs first.
// Pure, shared by both apps because both render the CTA for the same rows and
// the same endpoint (`POST /api/orders/mark-received`) decides. Web reaches it
// through `lib/orders/{normalize-status,can-mark-received}.ts`; mobile calls it
// directly. Same precedent as impression-buffer-core.
export * from "./seller-order-receipt";

// Admin listing suspension (2026-08-05) — `unlisted_reason` sentinel axis on
// top of `is_unlisted` + POST /api/admin/listings/[id]/suspend request/response.
export * from "./admin-listing-suspension";

// Sell drafts (2026-08-05) — versioned `listing_drafts.payload` envelope +
// save/delete wire bodies for the web creation-flow "Save as draft" feature.
// Web canonical builder: lib/listings/listing-draft-api.ts; pure domain
// (serialize/parse/meaningful): lib/listings/sell-draft.ts.
export * from "./sell-draft";
